/**
 * Form data initialization and management
 */

import { loadState } from './localStorage.js';

export function initializeFormData(serverConfig = {}) {
    const savedState = loadState();

    // Server-provided data takes precedence over localStorage for data values,
    // but currentStep from localStorage is preserved
    const initialStep = savedState?.currentStep || 1;

    const formData = {
        currentStep: initialStep,
        totalSteps: 8,
        isTransitioning: false,
        isSubmitting: false,
        showContent: false,
        isInitializing: true,

        // Form fields - prioritize saved state, fall back to server config
        name: savedState?.name || serverConfig.name || '',
        email: savedState?.email || serverConfig.email || '',
        day: savedState?.day || (serverConfig.day ? String(serverConfig.day).padStart(2, '0') : ''),
        month: savedState?.month || (serverConfig.month ? String(serverConfig.month).padStart(2, '0') : ''),
        year: savedState?.year || (serverConfig.year ? String(serverConfig.year) : ''),
        sex: savedState?.sex || serverConfig.sex || '',
        username: savedState?.username || serverConfig.username || '',
        password: '', // Never restored from localStorage for security
        cityId: savedState?.cityId || serverConfig.cityId || '',
        cityName: savedState?.cityName || serverConfig.cityName || '',
        cityCountryIso: savedState?.cityCountryIso || serverConfig.cityCountryIso || '',
        homeCityId: savedState?.homeCityId || serverConfig.homeCityId || '',
        homeCityName: savedState?.homeCityName || serverConfig.homeCityName || '',
        homeCityCountryIso: savedState?.homeCityCountryIso || serverConfig.homeCityCountryIso || '',

        // Validation - always start fresh, don't restore from localStorage
        errors: {},
        touched: {},
        // Per-field flag set on first user input. Stays true once flipped.
        // Step inputs gate the `touched` promotion on this so that focus
        // passing through an untouched empty field does not surface an
        // error, but a field that has held content keeps surfacing errors
        // after being cleared.
        dirty: {},

        // Geolocation
        locationRequested: savedState?.locationRequested || false,
        locationReady: savedState?.locationReady || false,
        userLat: savedState?.userLat || null,
        userLon: savedState?.userLon || null
    };

    // Apply server config overrides (except currentStep)
    Object.keys(serverConfig).forEach(key => {
        if (key === 'currentStep') return; // Preserve from localStorage
        if (key === 'errors') {
            // Handle errors specially
            formData.errors = parseErrors(serverConfig.errors);
            return;
        }
        if (serverConfig[key] !== undefined && serverConfig[key] !== null && serverConfig[key] !== '') {
            formData[key] = serverConfig[key];

            // Ensure proper formatting for date fields
            if (key === 'day' && serverConfig[key]) {
                formData.day = String(serverConfig[key]).padStart(2, '0');
            }
            if (key === 'month' && serverConfig[key]) {
                formData.month = String(serverConfig[key]).padStart(2, '0');
            }
            if (key === 'year' && serverConfig[key]) {
                formData.year = String(serverConfig[key]);
            }
        }
    });

    return formData;
}

function parseErrors(errors) {
    if (!errors) return {};

    try {
        if (typeof errors === 'string') {
            return JSON.parse(errors);
        }
        return errors;
    } catch (e) {
        console.error('[Signup] Error parsing errors:', e);
        return {};
    }
}
